import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { SpringsOfficeMap } from "@/components/SpringsOfficeMap";
import { RegionalHubLinks } from "@/components/RegionalHubLinks";
import { seoHead } from "@/components/hubs/HubPages";
import {
  SITE_URL, SCHEMA_LOCALBUSINESS_ID, BUSINESS_NAME, BUSINESS_PHONE_E164, BUSINESS_PHONE_DISPLAY, BUSINESS_PHONE_TEL,
  BUSINESS_STREET, BUSINESS_LOCALITY, BUSINESS_REGION, BUSINESS_POSTAL, BUSINESS_COUNTRY, BUSINESS_ADDRESS_FULL,
} from "@/lib/entitySchema";
import officePhoto1 from "@/assets/office-woodmen-1.jpg";
import officePhoto2 from "@/assets/office-woodmen-2.jpg";
import officePhoto3 from "@/assets/office-woodmen-3.jpg";

const HOURS: { day: string; time: string }[] = [
  { day: "Monday – Friday", time: "9:00 AM – 4:00 PM" },
  { day: "Saturday", time: "9:00 AM – 1:00 PM" },
  { day: "Sunday", time: "Closed" },
];

const OFFICE_PHOTOS = [
  { src: officePhoto1, alt: "Colorado Web Impressions office on Woodmen Rd, Colorado Springs" },
  { src: officePhoto2, alt: "Inside the Colorado Web Impressions Colorado Springs office" },
  { src: officePhoto3, alt: "Colorado Web Impressions workspace, Woodmen Rd Colorado Springs" },
];

const PATH = "/colorado-springs-office-woodmen-rd";
const TITLE = "Colorado Web Impressions on Woodmen Rd | Google Profile & Listings";
const DESC = "Find Colorado Web Impressions at 3210 E Woodmen Rd, Colorado Springs. See our Google Business Profile, directions, reviews and every official business listing.";
const GBP_URL = "https://maps.app.goo.gl/HgGdpsS4vNubWWk19";

const CITATIONS: { group: string; items: { label: string; url: string }[] }[] = [
  { group: "Google & maps", items: [
    { label: "Google Business Profile", url: GBP_URL },
    { label: "MapQuest", url: "https://www.mapquest.com/us/colorado/colorado-web-impressions-454269381" },
  ] },
  { group: "Reviews & trust", items: [
    { label: "Better Business Bureau", url: "https://www.bbb.org/us/co/colorado-springs/profile/web-design/colorado-web-impressions-0785-87368646" },
    { label: "Yelp", url: "https://www.yelp.com/biz/colorado-web-impressions-colorado-springs" },
    { label: "Trust Analytica", url: "https://trustanalytica.org/us/co/colorado-springs/reviews/colorado-web-impressions" },
  ] },
  { group: "Business directories", items: [
    { label: "Clutch", url: "https://clutch.co/profile/colorado-web-impressions" },
    { label: "DesignRush", url: "https://www.designrush.com/agency/profile/colorado-web-impressions" },
    { label: "TopDevelopers", url: "https://www.topdevelopers.co/profile/coloradowebimpressions" },
    { label: "The Org", url: "https://theorg.com/org/colorado-web-impressions" },
  ] },
  { group: "Social profiles", items: [
    { label: "Facebook", url: "https://www.facebook.com/coloradowebimpressions/" },
    { label: "Instagram", url: "https://www.instagram.com/coloradowebimpressions/" },
    { label: "LinkedIn", url: "https://www.linkedin.com/company/colorado-web-impressions/" },
    { label: "YouTube", url: "https://www.youtube.com/channel/UCobHjvCeHGsOsHFDp9cAOlQ" },
  ] },
];

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": SCHEMA_LOCALBUSINESS_ID,
    name: BUSINESS_NAME,
    url: SITE_URL,
    telephone: BUSINESS_PHONE_E164,
    email: "info@coloradowebimpressions.com",
    address: { "@type": "PostalAddress", streetAddress: BUSINESS_STREET, addressLocality: BUSINESS_LOCALITY, addressRegion: BUSINESS_REGION, postalCode: BUSINESS_POSTAL, addressCountry: BUSINESS_COUNTRY },
    geo: { "@type": "GeoCoordinates", latitude: 38.9359976, longitude: -104.7653118 },
    hasMap: GBP_URL,
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "16:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "13:00" },
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "149" },
    sameAs: CITATIONS.flatMap((g) => g.items.map((i) => i.url)),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Colorado Springs Headquarters", item: `${SITE_URL}/colorado-springs-headquarters` },
      { "@type": "ListItem", position: 3, name: "Woodmen Rd Office", item: `${SITE_URL}${PATH}` },
    ],
  },
].map((s) => ({ type: "application/ld+json", children: JSON.stringify(s) }));

export const Route = createFileRoute("/colorado-springs-office-woodmen-rd")({
  head: () => seoHead(PATH, TITLE, DESC, "website", SCHEMA),
  component: OfficePage,
});

function OfficePage() {
  return (
    <ArticleLayout
      eyebrow="Woodmen Rd · Colorado Springs"
      title="Our Colorado Springs office on Woodmen Rd"
      intro="Colorado Web Impressions is a real local business with a verified Google Business Profile. Here's where to find us, how to leave a review, and every official listing so you know it's really us."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Colorado Springs Headquarters", to: "/colorado-springs-headquarters" }, { label: "Woodmen Rd Office" }]}
      path={PATH}
      faqs={[
        { q: "Where is Colorado Web Impressions?", a: `${BUSINESS_ADDRESS_FULL}. Call ${BUSINESS_PHONE_DISPLAY}.` },
        { q: "Is Colorado Web Impressions on Google Maps?", a: "Yes. We have a verified Google Business Profile at our Woodmen Rd office in Colorado Springs." },
        { q: "How can I leave a review?", a: "Open our Google Business Profile and tap “Write a review.” Honest reviews help other Colorado businesses find us." },
      ]}
    >
      <h2>Name, address and phone</h2>
      <p>
        <strong>{BUSINESS_NAME}</strong><br />{BUSINESS_ADDRESS_FULL}<br />
        <a href={BUSINESS_PHONE_TEL}>{BUSINESS_PHONE_DISPLAY}</a>
      </p>
      <p>
        Rated <strong>5.0 out of 5</strong> from 149 Google reviews.{" "}
        <a href={GBP_URL} target="_blank" rel="noopener noreferrer">View our Google Business Profile and reviews</a>
      </p>

      <h2>Office hours</h2>
      <div className="not-prose my-4 overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <tbody>
            {HOURS.map((h) => (
              <tr key={h.day} className="border-b border-border last:border-0">
                <td className="px-4 py-2.5 font-medium text-foreground">{h.day}</td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{h.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-muted-foreground">Hours from our verified Google Business Profile. Call ahead for appointments outside these times.</p>

      <h2>Inside the office</h2>
      <div className="not-prose my-6 grid gap-4 sm:grid-cols-3">
        {OFFICE_PHOTOS.map((p) => (
          <img key={p.src} src={p.src} alt={p.alt} loading="lazy" className="aspect-[4/3] w-full rounded-xl border border-border object-cover" />
        ))}
      </div>

      <div className="not-prose my-8"><SpringsOfficeMap /></div>

      <h2>Our official business listings</h2>
      <p>These are the only official listings for Colorado Web Impressions. Each one shows the same name, address and phone.</p>
      <div className="not-prose grid gap-4 sm:grid-cols-2">
        {CITATIONS.map((g) => (
          <div key={g.group} className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-semibold text-foreground">{g.group}</h3>
            <ul className="mt-2 space-y-1 text-sm">
              {g.items.map((i) => (
                <li key={i.url}><a href={i.url} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-2 hover:underline">{i.label}</a></li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h2>What we do from Woodmen Rd</h2>
      <ul>
        <li><Link to="/colorado-springs-web-design">Colorado Springs web design</Link></li>
        <li><Link to="/colorado-springs-seo">Colorado Springs SEO</Link></li>
        <li><Link to="/colorado-springs-google-maps">Google Maps ranking</Link></li>
        <li><Link to="/colorado-springs-headquarters">Our 60-mile service area</Link></li>
      </ul>
      <RegionalHubLinks excludePath={PATH} />
    </ArticleLayout>
  );
}
